import type { GroupedItem } from "./grouped";
import { logger } from "../../../logger";

export type { CSVExportOptions } from "./core";

/**
 * Generates group summary data from grouped items
 */
export function generateGroupSummaryCSV(
  groupedData: GroupedItem[],
  groupingConfig?: {
    primaryField?: string;
    secondaryField?: string;
    primaryLabel?: string;
    secondaryLabel?: string;
  }
): string {
  if (!groupedData || groupedData.length === 0) {
    return "";
  }

  const csvRows: string[] = [];
  const summaryData: Array<{
    primaryGroup: string;
    secondaryGroup?: string;
    itemCount: number;
    level: number;
  }> = [];

  // Extract group headers and create summary
  groupedData.forEach((item) => {
    if (item.type === "group-header") {
      const parentKey = item.parentKey != null ? String(item.parentKey) : "";
      summaryData.push({
        primaryGroup: item.level === 1 ? item.groupLabel || "" : parentKey,
        secondaryGroup: item.level === 2 ? item.groupLabel || "" : undefined,
        itemCount: item.itemCount || 0,
        level: item.level || 1,
      });
    }
  });

  // Build headers
  const headers = ["Group_Level"];
  if (groupingConfig?.primaryField) {
    headers.push(groupingConfig.primaryLabel || `${groupingConfig.primaryField}_Group`);
  }
  if (groupingConfig?.secondaryField) {
    headers.push(groupingConfig.secondaryLabel || `${groupingConfig.secondaryField}_Group`);
  }
  headers.push("Item_Count");

  csvRows.push(headers.map((h) => `"${h}"`).join(","));

  // Add summary rows
  summaryData.forEach((summary) => {
    const values = [];
    values.push(`"Level ${summary.level}"`);

    if (groupingConfig?.primaryField) {
      values.push(`"${summary.primaryGroup}"`);
    }
    if (groupingConfig?.secondaryField) {
      values.push(`"${summary.secondaryGroup || ""}"`);
    }

    values.push(`"${summary.itemCount}"`);
    csvRows.push(values.join(","));
  });

  return csvRows.join("\n");
}

/**
 * Generates filename with entity name and date
 */
export function generateExportFilename(
  entityName: string,
  includeTimestamp: boolean = true,
  suffix?: string
): string {
  const date = new Date().toISOString().split("T")[0];
  const name = entityName.toLowerCase().replace(/\s+/g, "_");
  const suffixStr = suffix ? `_${suffix}` : "";
  return includeTimestamp
    ? `${name}_export${suffixStr}_${date}.csv`
    : `${name}_export${suffixStr}.csv`;
}

/** Cursor pagination info returned by a GraphQL collection response. */
export interface PageInfo {
  hasNextPage?: boolean;
  endCursor?: string | null;
}

/** Shape of a paginated GraphQL response consumed during export. */
export interface PaginatedResponse<TNode = Record<string, unknown>> {
  data?: {
    nodes?: TNode[];
    pageInfo?: PageInfo;
  } | null;
}

/**
 * Fetches all pages of data using cursor pagination with progress tracking
 * This is a reusable function that can be used across different export scenarios
 */
export async function fetchAllPagesWithProgress<TNode = Record<string, unknown>>(
  refetch: (variables: Record<string, unknown>) => Promise<PaginatedResponse<TNode>>,
  queryParams: Record<string, unknown>,
  onProgress?: (progress: number) => void,
  pageSize: number = 1000
): Promise<TNode[]> {
  const allData: TNode[] = [];
  let cursor: string | null = null;
  let pageCount = 0;
  const maxPages = 1000; // Safety limit

  try {
    // Start progress
    onProgress?.(10);

    while (pageCount < maxPages) {
      pageCount++;

      // Fetch page with cursor
      const response = await refetch({
        ...queryParams,
        after: cursor,
        first: pageSize,
      });

      if (!response?.data?.nodes || response.data.nodes.length === 0) {
        break;
      }

      allData.push(...response.data.nodes);

      // Update progress based on pages fetched
      // Estimate progress: 10% start + 40% for fetching (10-50%)
      const fetchProgress = 10 + Math.min(40, pageCount * 5);
      onProgress?.(Math.min(fetchProgress, 50));

      // Check if there are more pages
      if (!response.data.pageInfo?.hasNextPage) {
        break;
      }

      cursor = response.data.pageInfo?.endCursor || null;
    }

    // Progress to 50% after all pages fetched
    onProgress?.(50);
  } catch (error) {
    logger.error("Error fetching all pages", error);
    throw error;
  }

  return allData;
}

/** A pg_graphql-style collection (edges + pageInfo) as returned by a query. */
interface GraphqlCollection<TNode = Record<string, unknown>> {
  edges?: Array<{ node?: TNode | null } | null> | null;
  pageInfo?: PageInfo | null;
}

/** Map of the entity collections export queries may return. */
export type GraphqlCollectionResponse<TNode = Record<string, unknown>> = Partial<
  Record<
    | "contactsCollection"
    | "contact_tasksCollection"
    | "ticketsCollection"
    | "time_entriesCollection"
    | "app_usersCollection",
    GraphqlCollection<TNode> | null
  >
>;

/**
 * Extracts nodes and pageInfo from a GraphQL query response
 * Handles all entity types: contacts, tasks, tickets, time_entries, app_users
 */
export function extractNodesAndPageInfo<TNode = Record<string, unknown>>(
  responseData: GraphqlCollectionResponse<TNode> | null | undefined
): {
  nodes: TNode[];
  pageInfo: PageInfo | null;
} {
  const collection =
    responseData?.contactsCollection ??
    responseData?.contact_tasksCollection ??
    responseData?.ticketsCollection ??
    responseData?.time_entriesCollection ??
    responseData?.app_usersCollection;

  if (collection) {
    const nodes = (collection.edges ?? [])
      .map((edge) => edge?.node)
      .filter((node): node is TNode => node != null);
    return { nodes, pageInfo: collection.pageInfo ?? null };
  }

  return { nodes: [], pageInfo: null };
}
